import {
    CHUNK_SIZE,
    HP_DEFAULT,
    JUMP_HEIGHT,
    MAX_AIR_TICKS,
    MOVE_INTERVAL,
    PLAYER_HEIGHT_CROUCHING,
    PLAYER_HEIGHT_STANDING,
    PlayerState,
    TILE_SIZE,
    TileType,
    getRoomBoxes,
    isLadder,
    isPassable,
    UNDERGROUND_SPAWN_X,
    UNDERGROUND_SPAWN_Y,
} from '@blockworld/shared';
import { PositionQueue } from '../../network/PositionQueue';
import { VOICE_PROFILES } from '../../toki/TokiPonaAudio';
import { SpeechBubble } from '../../ui/SpeechBubble';
import type { GameClient } from '../../network/GameClient';

export interface GameSceneNetworkContext {
    networkClient: GameClient;
    [key: string]: any;
}

/**
 * Registers all NetworkClient callbacks for GameScene.
 *
 * Extracted from GameScene setupNetworkCallbacks as a move-only refactor.
 */
export function setupSceneNetworkCallbacks(scene: GameSceneNetworkContext): void {
    console.log('[SCENE] setupNetworkCallbacks() called');

    scene.networkClient.onInitialState = (localId, seed, players) => {
        console.log('[SCENE] onInitialState received:', { localId, seed, playerCount: players.length });
        scene.localPlayerId = localId;
        scene.worldSeed = seed;
        scene.roomBoxes = getRoomBoxes(seed);
        scene.initMentorBehavior?.();
        scene.initLoverBehavior?.();
        // Hero NPC is single-player only
        if (scene.networkClient.getMode() !== 'online') {
            scene.initHeroBehavior?.();
        }

        // Defensive cleanup: destroy any leftover sprites/queues from a
        // previous session that might have survived clearGameState().
        for (const [id, sprite] of scene.otherPlayerSprites) {
            sprite.destroy();
        }
        scene.otherPlayerSprites.clear();
        for (const [id, sprite] of scene.otherPlayerHeldBlocks) {
            sprite.destroy();
        }
        scene.otherPlayerHeldBlocks.clear();
        scene.otherPlayers.clear();

        // Capture the stale session ID set by clearGameState(). If our old
        // WebSocket close hasn't been processed by the server yet, the init
        // message will include our previous session as a "player". Filtering
        // it prevents a ghost clone of ourselves.
        // Only consume during online connections. During the intermediate
        // offline step of Online(A) → Offline → Online(B), we must preserve
        // the stale ID "A" so it's available when we reconnect as session B.
        const isOnline = scene.networkClient.getMode() === 'online';
        const staleId = isOnline ? scene.staleSessionId : null;
        if (isOnline) {
            scene.staleSessionId = null;
        }

        for (const p of players) {
            if (p.id === staleId) {
                // This is our own stale session from a previous connection.
                // The server hasn't processed our disconnect yet. Skip it —
                // a playerLeave will arrive shortly and would be a no-op.
                console.log(`[SCENE] Filtering stale self session ${staleId} from init`);
                continue;
            }

            if (p.id !== localId) {
                const queue = new PositionQueue();
                queue.push(p);
                scene.otherPlayers.set(p.id, queue);
            } else {
                scene.localPlayer = {
                    x: p.x,
                    y: p.y,
                    state: p.state,
                    facing: p.facing,
                    isCrouching: p.isCrouching,
                    isHoldingBlock: p.isHoldingBlock,
                    heldBlockType: p.heldBlockType >= 0 ? p.heldBlockType : null,
                    jumpRemaining: 0,
                    minJumpRemaining: 0,
                    jumpsquatStartTime: 0,
                    risingStartTime: 0,
                    lastRisingY: 0,
                    pendingLedgeDir: 0,
                    pendingBlockPlace: false,
                    pendingBlockPickup: false,
                    pendingAttackBlock: false,
                    pendingActionX: 0,
                    pendingActionY: 0,
                    // Combat
                    hp: p.hp ?? HP_DEFAULT,
                    maxHp: p.maxHp ?? HP_DEFAULT,
                    isHoldingSword: p.isHoldingSword ?? false,
                    swordHoldStartTick: 0,
                    lastSlashSpawnTick: 0,
                    lastSwordDestroyTick: -999,
                    pendingSlash: false,
                    jumpHeight: 0,
                    airTicks: MAX_AIR_TICKS,
                    equippedItem: null,
                    hasBoots: p.hasBoots ?? false,
                    bucketFull: false,
                    basketContents: [],
                    isGliding: false,
                    glideDir: 1,
                    spawnBlockX: -1,
                    spawnBlockY: -1,
                    thornbushTickCounter: 0,
                };
                scene.localPlayerSprite.setVisible(true);
                scene.combatHUD.updateHeartDisplay(scene.localPlayer!.hp, scene.localPlayer!.maxHp);
                scene.combatHUD.updateEquipmentDisplay(scene.localPlayer!.equippedItem);

                // Update sky color based on current mode (may have changed via teleport)
                scene.skybox.updateSkyColor(scene.networkClient.getMode() === 'online');

                // Start night music when joining multiplayer server
                scene.skybox.startNightMusic(scene.networkClient.getMode() === 'online');

                // Immediately request chunks around spawn point (don't wait for update loop)
                scene.chunkManager.requestSpawnChunks(p.x, p.y);
            }
        }

        // Dismiss portal transition overlay now that the new world is ready
        scene.hidePortalOverlay();
    };

    scene.networkClient.onPlayerPositions = (snapshots) => {
        // Skip if we're in transitional state (no local player set up yet)
        if (!scene.localPlayerId) return;

        const now = Date.now();
        for (const snap of snapshots) {
            if (snap.id === scene.localPlayerId) continue;

            const queue = scene.otherPlayers.get(snap.id);
            if (!queue) {
                // Don't create new entries here — only onPlayerJoin/onInitialState
                // should register players. Stale position broadcasts arriving after
                // onPlayerLeave would otherwise resurrect ghost players.
                continue;
            }
            queue.push({ ...snap, timestamp: now });
        }
    };

    scene.networkClient.onPlayerJoin = (player) => {
        // Skip if we're in transitional state (no local player set up yet)
        if (!scene.localPlayerId) return;
        if (player.id === scene.localPlayerId) return;
        const queue = new PositionQueue();
        queue.push({ ...player, timestamp: Date.now() });
        scene.otherPlayers.set(player.id, queue);
    };

    scene.networkClient.onPlayerLeave = (playerId) => {
        scene.otherPlayers.delete(playerId);
        const sprite = scene.otherPlayerSprites.get(playerId);
        if (sprite) {
            sprite.destroy();
            scene.otherPlayerSprites.delete(playerId);
        }
        const heldBlockGfx = scene.otherPlayerHeldBlocks.get(playerId);
        if (heldBlockGfx) {
            heldBlockGfx.destroy();
            scene.otherPlayerHeldBlocks.delete(playerId);
        }
        // Clean up sword sprite
        scene.combatHUD.removePlayer(playerId);
        // Clean up speech bubble
        const speechBubble = scene.otherPlayerSpeechBubbles.get(playerId);
        if (speechBubble) {
            speechBubble.destroy();
            scene.otherPlayerSpeechBubbles.delete(playerId);
        }
    };

    scene.networkClient.onChunkData = (chunks) => {
        for (const chunk of chunks) {
            const key = `${chunk.cx},${chunk.cy}`;
            scene.chunks.set(key, chunk.tiles);
        }
    };

    scene.networkClient.onBlockUpdate = (updates) => {
        // Skip player sync if we're in transitional state
        const canSyncPlayers = !!scene.localPlayerId;

        for (const update of updates) {
            // If block update includes player position, apply BOTH immediately
            // This ensures block and player appear at same moment
            const hasPlayerSync = canSyncPlayers &&
                                  update.playerId &&
                                  update.playerId !== scene.localPlayerId &&
                                  update.playerX !== undefined &&
                                  update.playerY !== undefined;

            if (hasPlayerSync) {
                const playerId = update.playerId!;
                const playerX = update.playerX!;
                const playerY = update.playerY!;

                // Apply block immediately (no queue)
                const cx = Math.floor(update.x / CHUNK_SIZE);
                const cy = Math.floor(update.y / CHUNK_SIZE);
                const chunkKey = `${cx},${cy}`;
                const chunk = scene.chunks.get(chunkKey);
                if (chunk) {
                    const localX = ((update.x % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                    const localY = ((update.y % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
                    chunk[localY][localX] = update.tileType;
                }

                const key = `${update.x},${update.y}`;
                if (update.action === 'place' && scene.isFallingType(update.tileType)) {
                    // Other players' placed blocks fall immediately (no grace period)
                    scene.localFallingBlocks.add(key);
                    // Track LOG inertia: prefer server-provided inertiaDir, fall back to playerFacing
                    if (update.tileType === TileType.LOG) {
                        const inertia = update.inertiaDir ?? update.playerFacing;
                        if (inertia !== undefined) {
                            scene.logInertia.set(key, inertia);
                        }
                    }
                } else if (update.action === 'pickup' || update.action === 'combine') {
                    scene.localFallingBlocks.delete(key);
                    scene.freshFallingBlocks.delete(key);
                    scene.logInertia.delete(key);
                    // Blocks above the removed block should start falling
                    scene.propagateLocalInstabilityUp(update.x, update.y - 1);
                }

                // Force player position immediately (only if player is known)
                // Don't create new entries here — only onPlayerJoin/onInitialState
                // should register players. Stale block updates arriving after
                // onPlayerLeave would otherwise resurrect ghost players.
                const queue = scene.otherPlayers.get(playerId);
                if (queue) {
                    queue.forcePosition({
                        id: playerId,
                        x: playerX,
                        y: playerY,
                        state: update.playerState ?? PlayerState.IDLE,
                        facing: update.playerFacing ?? 1,
                        isCrouching: update.playerIsCrouching ?? false,
                        isHoldingBlock: update.playerIsHoldingBlock ?? false,
                        heldBlockType: update.playerHeldBlockType ?? -1,
                        hp: HP_DEFAULT,
                        maxHp: HP_DEFAULT,
                        isHoldingSword: false,
                        timestamp: Date.now(),
                        sequence: Date.now(),
                    });
                }
            } else {
                // No player sync - queue for global tick (local player's own blocks or tree-chop)
                scene.pendingServerBlockUpdates.push({
                    x: update.x,
                    y: update.y,
                    tileType: update.tileType,
                    action: update.action,
                    inertiaDir: update.inertiaDir,
                    isLocalEcho: update.playerId === scene.localPlayerId,
                });
            }
        }
    };

    // Handle rollback from server (race condition resolution)
    scene.networkClient.onBlockActionRollback = (data) => {
        if (!scene.localPlayer) return;

        console.warn('Block action rolled back:', data);

        if (data.action === 'pickup' && data.reason === 'block_not_found') {
            // We tried to pick up a block that wasn't there
            // Could be: someone else got it, or it's at different Y due to fall timing
            // Take away the block we thought we picked up
            if (scene.localPlayer.isHoldingBlock) {
                scene.localPlayer.isHoldingBlock = false;
                scene.localPlayer.heldBlockType = null;
            }
            // DON'T set tile to SKY - block might be at Y+1 or Y-1
            // Server broadcasts will correct world state
        } else if (data.action === 'place' && data.reason === 'spot_occupied') {
            // We tried to place a block where something already exists
            // Give us back the block we thought we placed
            if (!scene.localPlayer.isHoldingBlock && data.blockType !== undefined) {
                scene.localPlayer.isHoldingBlock = true;
                scene.localPlayer.heldBlockType = data.blockType;
            }
            // Remove our optimistically placed block
            scene.setLocalTile(data.x, data.y, TileType.SKY);
        }
    };

    // Handle air strafe from other players (immediate horizontal movement)
    scene.networkClient.onAirStrafe = (playerId, dir) => {
        if (!scene.localPlayerId) return;
        const queue = scene.otherPlayers.get(playerId);
        if (queue) {
            queue.applyAirStrafe(dir);
        }
    };

    // Combat callbacks
    scene.networkClient.onSlash = (slash) => {
        // For local player's slashes, we already created a local prediction
        // Just update the ID to match server's ID (for cleanup sync)
        if (slash.ownerId === scene.localPlayerId) {
            // Find our local prediction slash and update its ID
            // Local predictions have negative IDs
            for (const [localId, localSlash] of scene.activeSlashes) {
                if (localId < 0 && localSlash.ownerId === scene.localPlayerId) {
                    // Transfer to server's ID
                    scene.activeSlashes.delete(localId);
                    scene.activeSlashes.set(slash.id, localSlash);
                    // Transfer sprite too
                    const sprite = scene.slashSprites.get(localId);
                    if (sprite) {
                        scene.slashSprites.delete(localId);
                        scene.slashSprites.set(slash.id, sprite);
                    }
                    return;
                }
            }
            // No local prediction found - DON'T create duplicate!
            // Local prediction will be created by continuous slash spawning
            // Creating here with Date.now() would cause phase mismatch
            console.log(`[SLASH SKIP] Server slash for local player, no prediction to reconcile`);
            return;
        }

        // For other players' slashes, use server position
        // Use TIME not tick for proper first-tick duration
        scene.activeSlashes.set(slash.id, {
            x: slash.spawnX,
            y: slash.spawnY,
            spawnX: slash.spawnX,
            spawnY: slash.spawnY,
            facing: slash.facing,
            spawnTime: Date.now(),  // Use current time as baseline
            duration: slash.duration,
            ownerId: slash.ownerId,
            lastCheckedX: slash.spawnX,  // Server already checked spawn position
        });
    };

    scene.networkClient.onDamage = (playerId, hp, source) => {
        const currentTick = Math.floor(Date.now() / MOVE_INTERVAL);
        if (playerId === scene.localPlayerId && scene.localPlayer) {
            scene.localPlayer.hp = hp;
            scene.combatHUD.updateHeartDisplay(scene.localPlayer!.hp, scene.localPlayer!.maxHp);
            scene.hurtUntilTick = currentTick + 2;
            // Play "o" hurt sound with speech bubble
            scene.showHurtBubble(playerId);
        } else {
            scene.otherPlayerHurtUntilTick.set(playerId, currentTick + 2);
            scene.showHurtBubble(playerId);
        }
    };

    scene.networkClient.onMaxHpUpdate = (maxHp, hp) => {
        if (scene.localPlayer) {
            scene.localPlayer.maxHp = maxHp;
            scene.localPlayer.hp = hp;
            scene.combatHUD.updateHeartDisplay(scene.localPlayer.hp, scene.localPlayer.maxHp);
        }
    };

    scene.networkClient.onDeath = (playerId) => {
        if (playerId === scene.localPlayerId && scene.localPlayer) {
            // Stop night music and home music on death
            scene.skybox.stopNightMusic();
            scene.skybox.stopHomeMusic();

            // Play death sound effect
            if (scene.cache.audio.exists('death_sound')) {
                scene.sound.play('death_sound', { volume: 0.2 });
            }

            // Start Zelda-style death animation
            // If online, pre-buffer the teleport back to offline world.
            // Don't rely on the server's teleport message — it can race with
            // the death message and fire before the death animation starts.
            if (scene.networkClient.getMode() === 'online') {
                scene.pendingDeathTeleport = {
                    destination: { worldId: 'local', isRemote: false, spawnX: UNDERGROUND_SPAWN_X, spawnY: UNDERGROUND_SPAWN_Y },
                    portalCode: [],
                };
            } else {
                scene.pendingDeathTeleport = null;
            }
            // Capture player screen position for freeze
            const screenCenterX = scene.cameras.main.width / 2;
            const screenCenterY = scene.cameras.main.height / 2;
            const playerScreenX = screenCenterX - TILE_SIZE / 2;
            const playerScreenY = screenCenterY - TILE_SIZE;

            // Clear local player's active slashes — dead players have no projectiles.
            // Other players' slashes remain and will expire via updateSlashes().
            for (const [id, slash] of scene.activeSlashes) {
                if (slash.ownerId === scene.localPlayerId) {
                    const sprite = scene.slashSprites.get(id);
                    if (sprite) {
                        sprite.destroy();
                        scene.slashSprites.delete(id);
                    }
                    scene.activeSlashes.delete(id);
                }
            }

            // Compute ground screen Y for skull landing
            const height = scene.localPlayer.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            const feetWorldY = scene.localPlayer.y + height; // tile row just below feet
            let groundWorldY = feetWorldY;
            for (let dy = 0; dy < 50; dy++) {
                const tile = scene.getTile(scene.localPlayer.x, feetWorldY + dy);
                if (!isPassable(tile) && !isLadder(tile)) {
                    groundWorldY = feetWorldY + dy;
                    break;
                }
                if (dy === 49) groundWorldY = feetWorldY + 50;
            }
            const groundScreenY = playerScreenY + (groundWorldY - scene.localPlayer.y) * TILE_SIZE - TILE_SIZE;
            scene.deathAnim.start(playerScreenX, playerScreenY, groundScreenY);
        } else {
            // Other player died - remove from map temporarily
            // Server will send respawn position
        }
    };

    scene.networkClient.onRuneUpdate = (x, y, hieroglyphIndex) => {
        const key = `${x},${y}`;
        scene.runeData.set(key, hieroglyphIndex);
    };

    scene.networkClient.onTeleport = (destination, portalCode) => {
        console.log(`[TELEPORT CALLBACK] Portal [${portalCode.join(',')}] -> ${destination.worldId}`);

        // If death animation or death screen is showing, buffer the teleport for after dismissal
        if (scene.deathScreenOpen || scene.deathAnim.isActive) {
            scene.pendingDeathTeleport = { destination, portalCode };
            return;
        }

        // Play teleport sound effect
        if (scene.cache.audio.exists('teleport_sound')) {
            scene.sound.play('teleport_sound', { volume: 0.2 });
        }

        // Show portal overlay immediately before transition begins
        scene.showPortalOverlay();

        // Teleport to destination - teleportTo will handle clearing state if it proceeds
        scene.networkClient.teleportTo(destination, () => scene.clearGameState()).catch((error) => {
            console.error('[TELEPORT CALLBACK] teleportTo failed:', error);
            scene.hidePortalOverlay();
        });
    };

    scene.networkClient.onWorldReset = (seed) => {
        console.log(`[WORLD RESET] Server world reset with new seed: ${seed}`);
        scene.worldSeed = seed;
        scene.roomBoxes = getRoomBoxes(seed);

        // Clear chunks and tiles (but keep player)
        scene.chunks.clear();
        scene.chunkManager.reset();

        // Clear tile sprites
        for (const [key, sprite] of scene.tileSprites) {
            sprite.destroy();
        }
        scene.tileSprites.clear();
        scene.activeTileKeys.clear();

        // Clear rune data
        scene.runeData.clear();

        // Clear mice
        scene.clientMouseSim.clear();
        for (const [id, sprite] of scene.mouseSprites) {
            sprite.destroy();
        }
        scene.mouseSprites.clear();

        console.log(`[WORLD RESET] Cleared local cache, will request fresh chunks`);
    };

    // Speech callback - display speech bubbles above players
    scene.networkClient.onSpeech = (playerId, words, voice) => {
        const isLocal = playerId === scene.localPlayerId;
        console.log(`[SPEECH] received: playerId=${playerId} isLocal=${isLocal} words=[${words}] voice=${voice}`);

        // Compute camera offset for converting world to screen coords
        const { offsetX, offsetY } = scene.getCameraOffset();
        const profile = (voice && voice in VOICE_PROFILES) ? VOICE_PROFILES[voice] : undefined;

        if (isLocal) {
            // Local player speech bubble
            if (scene.localPlayer) {
                // Remove existing bubble if any
                if (scene.localSpeechBubble) {
                    scene.localSpeechBubble.destroy();
                }
                // Convert to screen position
                const screenX = scene.localPlayer.x * TILE_SIZE + offsetX;
                const screenY = scene.localPlayer.y * TILE_SIZE + offsetY;
                scene.localSpeechBubble = new SpeechBubble({
                    scene: scene as unknown as Phaser.Scene,
                    words,
                    playerScreenX: screenX,
                    playerScreenY: screenY,
                    audio: scene.tokiAudio,
                    profile,
                });
            } else {
                console.warn('[SPEECH] local player ref is null, cannot create bubble');
            }
        } else {
            // Other player speech bubble
            const queue = scene.otherPlayers.get(playerId);
            const pos = queue?.getCurrentPosition();
            console.log(`[SPEECH] other player: queue=${!!queue} pos=${JSON.stringify(pos)} otherPlayers.size=${scene.otherPlayers.size}`);
            if (pos) {
                // Remove existing bubble if any
                const existingBubble = scene.otherPlayerSpeechBubbles.get(playerId);
                if (existingBubble) {
                    existingBubble.destroy();
                }
                // Convert to screen position
                const screenX = pos.x * TILE_SIZE + offsetX;
                const screenY = pos.y * TILE_SIZE + offsetY;
                const bubble = new SpeechBubble({
                    scene: scene as unknown as Phaser.Scene,
                    words,
                    playerScreenX: screenX,
                    playerScreenY: screenY,
                    audio: scene.tokiAudio,
                    profile,
                });
                scene.otherPlayerSpeechBubbles.set(playerId, bubble);
            } else {
                console.warn(`[SPEECH] cannot create bubble for ${playerId}: no position data`);
            }
        }
    };

    // Mouse/rodent enemy callbacks
    scene.networkClient.onMouseSpawn = (id, x, y, facing, rodentType, wallDir, vertDir) => {
        const mouse = scene.clientMouseSim.spawnMouse(x, y, facing, rodentType);
        mouse.id = id; // Use server-assigned ID directly on the returned entity
        mouse.wallDir = wallDir;
        mouse.vertDir = vertDir;
    };

    scene.networkClient.onMouseDeath = (id) => {
        scene.clientMouseSim.removeMouse(id);
        const sprite = scene.mouseSprites.get(id);
        if (sprite) {
            sprite.destroy();
            scene.mouseSprites.delete(id);
        }
    };

    // Mouse left our subscribed chunks (still alive server-side, just out of view)
    scene.networkClient.onMouseDespawn = (id) => {
        scene.clientMouseSim.removeMouse(id);
        const sprite = scene.mouseSprites.get(id);
        if (sprite) {
            sprite.destroy();
            scene.mouseSprites.delete(id);
        }
    };

    // Block spawn (speech command put a block in player's hand)
    scene.networkClient.onBlockSpawn = (blockType) => {
        if (scene.localPlayer) {
            scene.localPlayer.isHoldingBlock = true;
            scene.localPlayer.heldBlockType = blockType;
        }
    };

    // Jump height update (boots pickup)
    scene.networkClient.onJumpHeightUpdate = (jumpHeight) => {
        const alreadyHadBoots = scene.localPlayer?.hasBoots ?? false;
        if (scene.localPlayer) {
            scene.localPlayer.jumpHeight = jumpHeight;
            scene.localPlayer.hasBoots = true;
        }
        // Only play sound on a genuine new pickup, not a portal re-sync
        if (!alreadyHadBoots) {
            if (scene.cache.audio.exists('equip_boots')) {
                const equipSound = scene.sound.add('equip_boots', { volume: 0.35 });
                equipSound.once('complete', () => {
                    equipSound.destroy();
                    if (scene.cache.audio.exists('got_item')) {
                        scene.sound.play('got_item', { volume: 0.2 });
                    }
                });
                equipSound.play();
            } else if (scene.cache.audio.exists('got_item')) {
                scene.sound.play('got_item', { volume: 0.2 });
            }
        }
    };

    // Yellow key pickup (key tile is consumed from world, so this won't re-fire on portal re-sync)
    scene.networkClient.onYellowKeyPickup = () => {
        if (scene.cache.audio.exists('golden_key')) {
            scene.sound.play('golden_key', { volume: 0.15 });
        }
    };

    // Server-authoritative air state (drives bubble HUD display)
    scene.networkClient.onAirUpdate = (airTicks) => {
        if (scene.localPlayer) {
            scene.localPlayer.airTicks = airTicks;
            scene.combatHUD.updateBubbleDisplay(airTicks);
        }
    };

    // Pressure plate state changes (KIWEN_LA)
    scene.networkClient.onPressurePlateChanged = (x, y, active) => {
        const wd = scene.networkClient.worldData;
        if (wd) {
            if (active) {
                wd.pressurePlateActive.add(`${x},${y}`);
            } else {
                wd.pressurePlateActive.delete(`${x},${y}`);
            }
        }
    };

    // Sign glyph updates (KIWEN_NIMI)
    scene.networkClient.onSignUpdate = (x, y, glyphIndex) => {
        const wd = scene.networkClient.worldData;
        if (wd) {
            wd.setSignGlyph(x, y, glyphIndex);
        }
    };

    // Eye switch activation (KIWEN_LUKIN)
    scene.networkClient.onEyeSwitchActivated = (x, y, tick) => {
        const wd = scene.networkClient.worldData;
        if (wd) {
            wd.activateEyeSwitch(x, y, tick);
        }
    };

    // Spawn point set confirmation (KIWEN_MI)
    scene.networkClient.onSpawnPointSet = (x, y) => {
        if (scene.localPlayer) {
            scene.localPlayer.spawnBlockX = x;
            scene.localPlayer.spawnBlockY = y;
        }
    };

    // Custom respawn at KIWEN_MI block (instead of death/disconnect)
    scene.networkClient.onRespawnAt = (x, y) => {
        if (scene.localPlayer) {
            scene.localPlayer.x = x;
            scene.localPlayer.y = y;
            scene.localPlayer.hp = scene.localPlayer.maxHp;
            scene.localPlayer.state = PlayerState.FALLING;
            scene.combatHUD.updateHeartDisplay(scene.localPlayer.hp, scene.localPlayer.maxHp);
            // Request chunks around the new spawn position immediately,
            // otherwise the player falls through unloaded terrain
            scene.chunkManager.requestSpawnChunks(x, y);
        }
    };

    // Server load level indicator (0=healthy, 1=degraded, 2=critical)
    scene.networkClient.onServerLoad = (level: number) => {
        scene.serverLoadLevel = level;
        if (scene.combatHUD) {
            scene.combatHUD.setServerLoad(level);
        }
    };

}
